import { useState } from "react";
import Typography from "../components/Typography";
import Button from "../components/Button";
import { useOrganizationContext } from "../api/current-organization/organizationContext";
import { useTalentGigInvites } from "../api/talentGigInvites";
import { successToast } from "../toast";
import DialogBox from "../components/DialogBox";
import GeneralFormError from "./GeneralFormError";
import ApiError from "../api/common/apiError";
import useCurrentUser from "../auth/useCurrentUser";
import { dialogWidths } from "./DialogBox/DialogBox";

export type GigChangesDeclineButtonProps = {
    gigInviteId: string;
    className?: string;
    mutate: () => void
};

export const GigChangesDeclineButton= ({
    gigInviteId,
    className,
    mutate
}: GigChangesDeclineButtonProps) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { declineGigChanges, isDecliningChanges } = useTalentGigInvites();
    const { gigTerminology } = useOrganizationContext();
    const [submissionError, setSubmissionError] = useState<ApiError | undefined>(undefined);
    const { calendarReady } = useCurrentUser();

    const handleDecline = async () => {
        if (!gigInviteId || !calendarReady) return;

        setIsSubmitting(true);

        const response = await declineGigChanges(gigInviteId);

        setIsSubmitting(false);

        if (response.success) {
            mutate();
            setIsDialogOpen(false);
            successToast("Changes declined. Event has been cancelled.");
            return;
        }

        setSubmissionError(response.error);
    };

    return (
        <span className={className}>
            <Button
                className="w-full sm:w-auto text-center sm:flex-grow" 
                variant="secondary" 
                onClick={() => setIsDialogOpen(true)}
                disabled={!gigInviteId || isDecliningChanges}
                loading={!gigInviteId}
            >
                Decline changes
            </Button>
            <DialogBox
                isOpen={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                title={`Decline ${gigTerminology.toLowerCase()} changes`}
                maxWidth={dialogWidths.medium}
            >
                <GeneralFormError error={submissionError} className="ml-8" />
                <div className="w-full space-y-6 flex flex-col">
                    {calendarReady && (
                        <>
                            <Typography variant="body" component="p">Are you sure you want to decline these changes?</Typography>
                            <Typography variant="body" component="p" className="max-w-sm">
                                Confirming this action will remove you from this {gigTerminology.toLowerCase()} and delete the event from your calendar.
                            </Typography>
                        </>
                    )}
                    {!calendarReady && (
                        <Typography variant="body" component="p" className="text-error">
                            Connect your calendar before declining (required to sync schedule).
                        </Typography>
                    )}
                    <div className="flex flex-col-reverse md:flex-row gap-4">                        
                        <Button
                            className="mx-auto w-full"
                            type="button"
                            disabled={isSubmitting}
                            onClick={() => setIsDialogOpen(false)}
                            variant="secondary"
                        >
                            Cancel
                        </Button>
                        <Button
                            className="mx-auto w-full"
                            type="submit"
                            loading={isSubmitting}
                            onClick={handleDecline}
                            disabled={!calendarReady}
                        >
                            Decline changes
                        </Button>
                    </div>
                </div>
            </DialogBox>
        </span>
    );
};